/**
 * Prioritization Layout
 * 
 * Main layout wrapper with:
 * - New collapsible sidebar navigation
 * - Breadcrumb trail
 * - Cycle context bar (when inside a cycle)
 * - Mode indicator
 * 
 * Now supports LayoutV2 via feature flag for new navigation experience.
 */

import { type ReactNode, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sparkles, X, Building2, Search, LogOut } from 'lucide-react';
import { CycleContextBar } from './CycleContextBar';
import { CommandPalette } from './CommandPalette';
import { Sidebar } from './Sidebar';
import { PlanningSidebar } from '../planning/PlanningSidebar';
import { PrioritizationAppFooter } from './PrioritizationAppFooter';
import {
  isPrioritizationSettingsPath,
  isSettingsOpenedFromPlanning,
  setSettingsEntryProduct,
} from '../../lib/productSurfacePaths';
import { useSettingsEntryProduct } from '../../hooks/useSettingsEntryProduct';
import { DualBreadcrumb, useBreadcrumbHistoryTracker } from '../Breadcrumb';
import { NotificationToast } from '../NotificationToast';
import { SyncStatusIndicator } from './SyncStatusIndicator';
import { NotificationBell } from './NotificationBell';
import { ToastProvider } from '../ui/Toast';
import { ThemeToggle } from '../ui/ThemeToggle';
import { HarryShell } from '../harry/HarryShell';
import { HarryHeaderActions } from '../harry/HarryHeaderActions';
import { isInDemoMode, isSupabaseAvailable, getCurrentWorkspaceId } from '../../lib/supabaseSync';
import { usePinnedCycleStore } from '../../stores/prioritization/pinnedCycleStore';
import { useFocusModeBarAligned } from '../../hooks/prioritization/useFocusModeBarAligned';
import { useFocusFollowsInstance } from '../../hooks/prioritization/useFocusFollowsInstance';
import { TossBallDrawer } from './catchball/TossBallDrawer';
import { getSupabaseClient } from '../../lib/supabaseClient';
import { clearTenantContext } from '../../lib/tenantContext';
import { useWorkspaceContextBootstrap } from '../../hooks/useWorkspaceContextBootstrap';
import { useWorkspaceScopedStores } from '../../hooks/useWorkspaceScopedStores';
import { useStoreErrorHandler } from '../../hooks/prioritization/useStoreErrorHandler';
import { useUserStore } from '../../stores/prioritization/userStore';
import { useTenantStore } from '../../stores/prioritization/tenantStore';
import { useUIStore } from '../../stores/prioritization/uiStore';
// Store imports for hydration
import { usePlanningDefaultsStore } from '../../stores/prioritization/planningDefaultsStore';
import { useResourceTypesStore } from '../../stores/prioritization/resourceTypesStore';
import { usePrioritizationTaskStore } from '../../stores/prioritization/prioritizationTaskStore';
import { useDegapProcessStore } from '../../stores/prioritization/degapProcessStore';
import { useSavedViewsStore } from '../../stores/prioritization/savedViewsStore';
import { useItemPoolsStore } from '../../stores/prioritization/itemPoolsStore';
import { useCycleConfigStore } from '../../stores/prioritization/cycleConfigStore';
import { useItemsStore } from '../../stores/prioritization/itemsStore';
import { useEvidenceStore } from '../../stores/prioritization/evidenceStore';
import { useTriageStore } from '../../stores/prioritization/triageStore';
import { useStrategyStore } from '../../stores/prioritization/strategyStore';
import { PRIORITIZATION_DASHBOARD_REFRESH_EVENT } from '../../lib/prioritizationDashboardRefreshEvents';
import { refreshPrioritizationDashboardAggregates } from '../../lib/refreshPrioritizationDashboardAggregates';
// ── Mobile experience (isolated, feature-flagged, fully rollback-safe) ──
import { useMobileExperience } from '../../mobile/featureFlag';
import { MobileShell } from '../../mobile/MobileShell';
import { AiAssistBootstrap } from './AiAssistBootstrap';
import { ReadinessRulesBootstrap } from './ReadinessRulesBootstrap';
import { ContributorLayout } from './ContributorLayout';
import { useContributorExperience } from '../../hooks/prioritization/useContributorExperience';
import { AINavigator } from '../AINavigator';
import { isAiCapabilityEnabled } from '../../lib/ai/aiAssistSettings';
import { useHarryStore } from '../../stores/prioritization/harryStore';
import { monoIconButton } from '@/components/mono/iconButton';

interface LayoutProps {
  children: ReactNode;
}

export default function PrioritizationLayout({ children }: LayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  // Keep Plans chrome when Settings was opened from Plans (shared settings hub).
  useSettingsEntryProduct();
  const usePlanningChrome = isSettingsOpenedFromPlanning(location.pathname);

  useEffect(() => {
    // Drop the Plans→Settings entry marker once the user leaves the settings hub.
    if (!isPrioritizationSettingsPath(location.pathname)) {
      setSettingsEntryProduct(null);
    }
  }, [location.pathname]);

  const [demoMode, setDemoMode] = useState(false);
  const [showDemoBanner, setShowDemoBanner] = useState(true);
  const [workspaceName, setWorkspaceName] = useState<string | null>(null);
  const [isWorkspaceDemo, setIsWorkspaceDemo] = useState(true);
  const [workspaceResolutionAttempted, setWorkspaceResolutionAttempted] = useState(false);
  const [cmdPaletteOpen, setCmdPaletteOpen] = useState(false);
  const currentRole = useUserStore((s) => s.currentRole);
  const signOut = useUserStore((s) => s.signOut);
  const sidebarOpen = useUIStore((s) => s.sidebarOpen);
  const { useContributorShell, shellReady } = useContributorExperience();

  // Mobile experience switch (must run unconditionally — it's a hook). Desktop
  // (>= 768px) and the kill-switch path return false, leaving everything below
  // exactly as it was.
  const useMobileShell = useMobileExperience();

  // Track navigation history for breadcrumb trail
  useBreadcrumbHistoryTracker();

  // Listen for store errors and display notifications
  useStoreErrorHandler();
  
  // Stage 7 Slice 2: Set workspace context from DB/auth on mount so getCurrentWorkspaceId() is
  // authoritative. The same bootstrap every product shell runs (one copy, three shells).
  useWorkspaceContextBootstrap({
    onContext: (ctx) => {
      const { setUserAvatarUrl } = useUserStore.getState();
      void (async () => {
        try {
          const supabase = getSupabaseClient();
          const { data: prof } = await supabase
            .from('user_rich_profiles')
            .select('avatar_url')
            .eq('user_id', ctx.userId)
            .maybeSingle();
          const raw = prof?.avatar_url as string | null | undefined;
          setUserAvatarUrl(typeof raw === 'string' && raw.length > 0 ? raw : null);
        } catch {
          // non-fatal — sidebar falls back to initials
        }
      })();
    },
    onSettled: () => setWorkspaceResolutionAttempted(true),
  });
  
  // Load current workspace info (re-runs when workspace context becomes available)
  const currentWorkspaceIdForEffect = useTenantStore((state) => state.currentWorkspaceId) ?? getCurrentWorkspaceId();
  // Reload permissions when workspace changes so resolvedCapabilityIds (DB role_permissions) is for current workspace
  useEffect(() => {
    if (currentWorkspaceIdForEffect) useUserStore.getState().loadPermissions?.();
  }, [currentWorkspaceIdForEffect]);
  // Load DB-backed planning stores (no-op if already loaded for this workspace)
  useEffect(() => {
    if (!currentWorkspaceIdForEffect) return;
    void usePlanningDefaultsStore.getState().load(currentWorkspaceIdForEffect);
    void useResourceTypesStore.getState().load(currentWorkspaceIdForEffect);
  }, [currentWorkspaceIdForEffect]);
  useEffect(() => {
    async function loadWorkspace() {
      const workspaceId = getCurrentWorkspaceId();
      if (!workspaceId) {
        setWorkspaceName(null);
        return;
      }
      try {
        const supabase = getSupabaseClient();
        const { data } = await supabase
          .from('workspaces')
          .select('name, is_demo')
          .eq('id', workspaceId)
          .single();
        if (data) {
          setWorkspaceName(data.name);
          setIsWorkspaceDemo(data.is_demo);
        }
      } catch (err) {
        console.error('[Layout] Error loading workspace:', err);
      }
    }
    loadWorkspace();
  }, [currentWorkspaceIdForEffect]);
  
  // Track if hydration has been done
  const hydrationDone = useRef(false);
  
  // LayoutV2 feature removed - using original layout
  
  // Hydrate stores from Supabase on mount
  useEffect(() => {
    if (hydrationDone.current) return;
    hydrationDone.current = true;
    
    const hydrateStores = async () => {
      if (!isSupabaseAvailable()) {
        console.log('[Layout] Supabase not available, skipping hydration');
        return;
      }
      
      console.log('[Layout] Hydrating stores from Supabase...');
      
      // Load all stores in parallel for better performance
      // Use optional chaining for methods that may not exist on all stores
      const userId = useUserStore.getState().userId;
      const hydrationPromises = [
        usePrioritizationTaskStore.getState().loadTasksFromSupabase?.(),
        useDegapProcessStore.getState().fetchFromSupabase?.(),
        useSavedViewsStore.getState().fetchFromSupabase?.(),
        useItemPoolsStore.getState().fetchFromSupabase?.(),
        useCycleConfigStore.getState().loadSessionsFromSupabase?.(),
        useItemsStore.getState().fetchFromSupabase?.(),
        useEvidenceStore.getState().fetchFromSupabase?.(),
        useTriageStore.getState().fetchFromSupabase?.(),
        useStrategyStore.getState().fetchFromSupabase?.(),
        useUserStore.getState().loadPermissions?.(),
        userId && userId !== 'unknown' ? useTenantStore.getState().fetchTenants(userId) : undefined,
      ].filter(Boolean);
      
      await Promise.all(hydrationPromises);
      
      // Set permission scope to current workspace so DB role assignments (e.g. workspace-admin, shaper) apply
      const workspaceId = getCurrentWorkspaceId();
      if (workspaceId) {
        useUserStore.getState().setCurrentScope?.({
          kind: 'resource',
          id: workspaceId,
          parentScope: { kind: 'organization', id: '*' },
        });
      }
      
      console.log('[Layout] Store hydration complete');
    };
    
    hydrateStores().catch(err => {
      console.error('[Layout] Store hydration failed:', err);
    });
  }, []);
  
  // Keep permission scope and workspace-scoped data in sync when workspace changes —
  // the same hook the Mandate shell runs for the platform pages it embeds.
  useWorkspaceScopedStores();

  const isInFocusMode = usePinnedCycleStore((s) => s.isInFocusMode);
  const focusModeBarAligned = useFocusModeBarAligned();
  useFocusFollowsInstance();

  // Check demo mode on mount and location change
  // NOTE: All hooks must be called before any conditional returns
  useEffect(() => {
    setDemoMode(isInDemoMode());
  }, [location.pathname]);

  // Cmd+K to open command palette
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCmdPaletteOpen(prev => !prev);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // After creates/updates/deletes (sync layer or explicit dispatch), reload dashboard aggregates
  // so widgets like Intake Queue stay aligned with Supabase without a full navigation.
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const onRefresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        const { workspaceResolutionComplete, currentWorkspaceId } = useTenantStore.getState();
        if (!workspaceResolutionComplete || !currentWorkspaceId) return;
        refreshPrioritizationDashboardAggregates();
      }, 120);
    };
    window.addEventListener(PRIORITIZATION_DASHBOARD_REFRESH_EVENT, onRefresh);
    return () => {
      clearTimeout(timer);
      window.removeEventListener(PRIORITIZATION_DASHBOARD_REFRESH_EVENT, onRefresh);
    };
  }, []);
  
  const exitDemoMode = () => {
    sessionStorage.removeItem('peakPriorities_demoMode');
    sessionStorage.removeItem('peakPriorities_demoWorkspaceId');
    window.location.href = '/prioritization';
  };
  
  // Add padding when demo banner is visible
  const topPadding = demoMode && showDemoBanner ? 'pt-10' : '';
  
  // LayoutV2 removed - using original layout only
  
  const focusMode = isInFocusMode() && focusModeBarAligned;

  // Full-viewport takeover surfaces (Cycle Designer) own the bottom edge of the
  // screen: floating AI launchers would cover their footer's next-step button,
  // and the app footer would push that button below the fold.
  const isTakeoverSurface = /^\/prioritization\/cycles\/[^/]+\/designer/.test(location.pathname);

  // Exclusive corner button: Guides XOR Co-Facilitator (never both).
  const cornerAssistant = useHarryStore((s) => s.uiPrefs.cornerAssistant);
  const coFacilitatorEnabled = isAiCapabilityEnabled('co_facilitator', getCurrentWorkspaceId());
  const showCoFacilitatorFab =
    !isTakeoverSurface &&
    coFacilitatorEnabled &&
    cornerAssistant === 'co_facilitator';

  if (useMobileShell) {
    return (
      <>
        {/* Guides open from the phone header too, and read the AI settings this loads. */}
        <AiAssistBootstrap />
        <ReadinessRulesBootstrap />
        <MobileShell>{children}</MobileShell>
      </>
    );
  }

  if (!shellReady) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--bg-raised)] dark:bg-[var(--bg)]">
        <div className="text-sm text-[var(--text-ter)]">Loading workspace…</div>
      </div>
    );
  }

  if (useContributorShell) {
    return (
      <ToastProvider>
        <AiAssistBootstrap />
        <ReadinessRulesBootstrap />
        <NotificationToast />
        <TossBallDrawer />
        <ContributorLayout>{children}</ContributorLayout>
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
    <AiAssistBootstrap />
    <ReadinessRulesBootstrap />
    <HarryShell suppressLauncher={isTakeoverSurface}>
    {/* The page ground stays white in focus mode. Gold and clay words on their own
        wash (chips, callouts, count badges) reach 4.5:1 only with white under the
        wash, so no visible tint can sit beneath them. The focus tint lives on the
        top bar alone, one layer. */}
    <div className={`pp-content-shell min-h-screen transition-colors duration-200 bg-[var(--bg-raised)] dark:bg-[var(--bg)] ${topPadding}`}>
      {/* Workspace Banner */}
      {demoMode && showDemoBanner && (
        <div className={`fixed top-0 left-0 right-0 z-50 text-white px-4 py-2 shadow-lg ${
          isWorkspaceDemo 
            ? 'bg-gradient-to-r from-amber-500 to-orange-500' 
            : 'bg-gradient-to-r from-emerald-600 to-teal-600'
        }`}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isWorkspaceDemo ? (
                <Sparkles className="h-5 w-5" />
              ) : (
                <Building2 className="h-5 w-5" />
              )}
              <span className="font-medium">
                {isWorkspaceDemo ? 'Demo Mode' : 'Workspace'}
              </span>
              <span className={isWorkspaceDemo ? 'text-[var(--mustard)]' : 'text-[var(--green)] dark:text-[var(--text-ter)]'}>
                {workspaceName ? `• ${workspaceName}` : ''}
                {isWorkspaceDemo && ' • Sample data'}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exitDemoMode}
                className="px-3 py-1 bg-[var(--bg-raised)]/20 hover:bg-[var(--bg-raised)]/30 rounded text-sm font-medium transition-colors"
              >
                {isWorkspaceDemo ? 'Exit Demo' : 'Exit'}
              </button>
              <button
                onClick={() => setShowDemoBanner(false)}
                className="p-1 hover:bg-[var(--bg-raised)]/20 rounded transition-colors"
                title="Hide banner"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Stage 7 Slice 2: Fail honestly when workspace cannot be resolved (non-demo, no auth context). Only show after initial resolution to avoid flash while workspace is loading. Platform/system admins have no workspace by design, so suppress for them. */}
      {workspaceResolutionAttempted && !demoMode && !getCurrentWorkspaceId() && currentRole !== 'platform-admin' && currentRole !== 'system-admin' && (
        <div className="bg-[var(--mustard-dim)] dark:bg-[var(--mustard)]/20 border-b border-[var(--mustard)] dark:border-[var(--mustard)]/50 px-4 py-2 text-sm text-[var(--mustard)] dark:text-[var(--mustard)] text-center">
          Your account isn't a member of any workspace yet. If someone invited you, open their invite link again; otherwise ask your workspace admin for an invite.
        </div>
      )}
      
      {/* Global Notifications */}
      <NotificationToast />

      {/* "Toss the ball" opens here, over the page you are on. */}
      <TossBallDrawer />

      {/* Command Palette */}
      <CommandPalette open={cmdPaletteOpen} onClose={() => setCmdPaletteOpen(false)} />
      
      {/* Sidebar — Plans chrome when Settings was opened from Plans */}
      {usePlanningChrome ? <PlanningSidebar /> : <Sidebar />}
      
      {/* Main content — padding tracks icon-rail vs full sidebar */}
      <div
        className={`flex flex-col min-h-screen overflow-x-hidden transition-[padding] duration-200 ${
          sidebarOpen ? 'pl-64' : 'pl-16'
        }`}
      >
        {/* Top bar — breadcrumb left, utilities right */}
        <div className={`pp-topbar border-b px-6 h-14 flex items-center justify-between gap-4 flex-shrink-0 transition-colors duration-200 ${focusMode ? 'bg-[var(--hover-tint)] dark:bg-[var(--bg-raised)] border-[var(--rule)] dark:border-[var(--rule)]' : 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] border-[var(--rule)] dark:border-[var(--rule)]'}`}>
          <DualBreadcrumb />
          <div className="flex items-center gap-1.5 flex-shrink-0 ml-auto">
            <HarryHeaderActions />
            <button
              type="button"
              onClick={() => setCmdPaletteOpen(true)}
              className={monoIconButton('sm')}
              title="Search (⌘K)"
              aria-label="Open command palette (⌘K)"
            >
              <Search className="h-4 w-4 shrink-0" />
            </button>
            <ThemeToggle variant="icon" />
            <NotificationBell />
            <SyncStatusIndicator />
            <button
              type="button"
              onClick={() => {
                clearTenantContext();
                signOut();
                navigate('/auth/login', { replace: true });
              }}
              className={monoIconButton('sm')}
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4 shrink-0" />
            </button>
          </div>
        </div>

        {/* Persistent focus mode bar — appears on every page when a cycle is active */}
        <CycleContextBar />

        {/* Page content */}
        <main className="flex-1 pt-[22px] pb-6 px-6">
          {children}
        </main>

        {!isTakeoverSurface && <PrioritizationAppFooter />}
      </div>
    </div>
    {!showCoFacilitatorFab ? null : <AINavigator />}
    </HarryShell>
    </ToastProvider>
  );
}
